import { useList } from "@refinedev/core";

import { TitrationMedByClass } from "@/constants/titration";
import { useTitrationPlan } from "@/contexts/TitrationPlanContext";
import { VITALL_DOC_SYSTEM } from "@/utils";
import { GdmtMedicationClass } from "@/utils/GraphQLClient/titration";
import { FhirResourceType, formatters } from "@/utils/fhir-utils";
import { emptyByClass } from "@/utils/titration";

import { fhirR4 } from "@smile-cdr/fhirts";

const ACTIVE_STATUS = "active";

const MED_STAT_CATEGORY_TO_MED_REQ_CATEGORY: Record<string, GdmtMedicationClass> = {
  acei: GdmtMedicationClass.Rasi,
  arbs: GdmtMedicationClass.Rasi,
  arnis: GdmtMedicationClass.Rasi,
  sglt2i: GdmtMedicationClass.Sglt2i,
  mras: GdmtMedicationClass.Mra,
  betaBlockers: GdmtMedicationClass.BetaBlocker,
};

export interface UsePatientMedStatementsResponse {
  medications: fhirR4.MedicationStatement[];
  titrationMedsByClass: TitrationMedByClass;
  isLoading: boolean;
  isError: boolean;
}

export const usePatientMedStatements = (fhirPatientId: string): UsePatientMedStatementsResponse => {
  const {
    result,
    query: { isError, isLoading },
  } = useList({
    resource: FhirResourceType.MedicationStatement,
    filters: [{ field: "patient", operator: "eq", value: formatters.formatPatient(fhirPatientId) }],
    pagination: { mode: "off" },
    meta: { fetchAllPages: true },
    queryOptions: { enabled: Boolean(fhirPatientId) },
  });

  const { isLoading: titrationLoading, titrationMedicationStatements } = useTitrationPlan();

  const fhirResources = result.data as fhirR4.Resource[] | undefined;

  const { allMedications, titrationMedsByClass } = (fhirResources ?? []).reduce<{
    allMedications: fhirR4.MedicationStatement[];
    titrationMedsByClass: TitrationMedByClass;
  }>(
    (acc, resource) => {
      if (!resource.id || resource.resourceType !== FhirResourceType.MedicationStatement) {
        return acc;
      }
      // PART 1: Get all patient's MedicationStatement resources
      const medStatement = resource as fhirR4.MedicationStatement;
      acc.allMedications.push(medStatement);

      // PART 2: Get all patient's titration MedicationStatement resources
      /** Return early if:
       * a) MedStat's status is not active
       * b) MedStat doesn't have a category
       */
      const categoryCode = medStatement.category?.coding?.find(({ system }) => system === VITALL_DOC_SYSTEM)?.code;
      if (medStatement.status !== ACTIVE_STATUS || !categoryCode) {
        return acc;
      }
      if (categoryCode in MED_STAT_CATEGORY_TO_MED_REQ_CATEGORY) {
        const medReqCode = MED_STAT_CATEGORY_TO_MED_REQ_CATEGORY[categoryCode];
        acc.titrationMedsByClass[medReqCode] = [...(acc.titrationMedsByClass[medReqCode] ?? []), medStatement];
      }
      return acc;
    },
    {
      allMedications: [],
      titrationMedsByClass: emptyByClass(),
    }
  );

  // Uncollapsed: includes a med discontinued and replaced within the same class, so its stopped
  // state overrides the stale (active) copy from the fetched list — the class-keyed map would only
  // retain the replacing med and leave the discontinued med looking unchanged.
  const planMedicationStatements = titrationMedicationStatements.filter(
    (ms): ms is fhirR4.MedicationStatement => !!ms?.id
  );

  // Merge in the titration plan's MedicationStatements, deduplicating by id.
  // Titration statements override list resources with the same id, and are appended when new.
  const titrationById = new Map(planMedicationStatements.map(ms => [ms.id, ms]));
  const overridden = allMedications.map(ms => titrationById.get(ms.id) ?? ms);
  const overriddenIds = new Set(allMedications.map(ms => ms.id));
  const newTitrationStatements = planMedicationStatements.filter(ms => !overriddenIds.has(ms.id));
  const mergedMedicationStatements = [...overridden, ...newTitrationStatements];

  return {
    medications: mergedMedicationStatements,
    titrationMedsByClass,
    isLoading: isLoading || titrationLoading,
    isError,
  };
};
